import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Search,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setHistories,
  setSelectedHistory,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  clearSelectedHistory,
} from '../../store/slices/queryHistorySlice';
import {
  GET_QUERY_HISTORY_LIST,
  GET_QUERY_HISTORY,
} from '../../graphql/queries/queryHistory.queries';
import {
  FindQueryHistoryListInput,
  FindQueryHistoryInput,
  QueryHistoryListResponse,
  QueryHistoryResponse,
  QueryHistory as QueryHistoryType,
} from '../../types/queryHistory.types';
import ViewQueryHistoryModal from '../../components/QueryHistory/ViewQueryHistoryModal';
import MainLayout from '../../components/Layout/MainLayout';
import './QueryHistory.css';

const truncateText = (text: string | null, maxLength: number = 80): string => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const QueryHistory: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { histories, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.queryHistory
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [fetchHistories, { loading: fetchingList }] = useLazyQuery<
    { chatbotQueryHistoryList: QueryHistoryListResponse },
    { params: FindQueryHistoryListInput }
  >(GET_QUERY_HISTORY_LIST, { fetchPolicy: 'network-only' });

  const [fetchHistory] = useLazyQuery<
    { chatbotQueryHistory: QueryHistoryResponse },
    { params: FindQueryHistoryInput }
  >(GET_QUERY_HISTORY, { fetchPolicy: 'network-only' });

  useEffect(() => {
    loadHistories();
  }, [pagination.page, pagination.pageSize, search, sort]);

  const loadHistories = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchHistories({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.chatbotQueryHistoryList?.success && data.chatbotQueryHistoryList.data) {
        dispatch(setHistories(data.chatbotQueryHistoryList.data));
      } else {
        dispatch(setError(data?.chatbotQueryHistoryList?.message || 'Error al cargar historial'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'desc' }));
    } else {
      dispatch(setSort({ field: 'createdAt', order: 'desc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchHistory({ variables: { params: { ids: [id] } } });
      if (data?.chatbotQueryHistory?.success && data.chatbotQueryHistory.data && data.chatbotQueryHistory.data.length > 0) {
        dispatch(setSelectedHistory(data.chatbotQueryHistory.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.chatbotQueryHistory?.message || 'Error al cargar registro'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedHistory());
  };

  const columns: GridColDef<QueryHistoryType>[] = [
    {
      field: 'query',
      headerName: 'Consulta',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string) => truncateText(value),
    },
    {
      field: 'generatedSql',
      headerName: 'SQL Generado',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string) => truncateText(value),
    },
    {
      field: 'prompt',
      headerName: 'Prompt',
      flex: 1,
      minWidth: 180,
      valueGetter: (value: string) => truncateText(value, 60),
    },
    {
      field: 'tablesUsed',
      headerName: 'Tablas Utilizadas',
      width: 200,
    },
    {
      field: 'executionSuccess',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.executionSuccess ? 'Exitoso' : 'Error'}
          color={params.row.executionSuccess ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="query-history-container">
        <Paper elevation={2} className="query-history-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Historial Queries</Typography>
          </Breadcrumbs>

          <Box className="query-history-header">
            <Typography variant="h4" component="h1">
              Historial de Queries
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registro de consultas SQL ejecutadas
            </Typography>
          </Box>

          <Box className="query-history-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar consulta..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={histories}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewQueryHistoryModal open={viewModalOpen} onClose={handleCloseViewModal} />
      </div>
    </MainLayout>
  );
};

export default QueryHistory;
